import { Grid, makeStyles } from "@material-ui/core";
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import MaintainenenceTableDataGrid from "./MaintainenenceTableDataGrid";
import * as d3 from "d3";
import {
  selectCompanyQuotaData,
  selectUpdatedRecords,
  clearUpdateQuotaData,
} from "./redux/management.slice";
import { bulkUpsertCompanyQuotaData } from "./redux/management.thunk";
import LoadingData from "./LoadingData";
import { _isNaN } from "../../util/customFunctions";
import { store } from "../../util/store";

const useStyle = makeStyles((theme) => ({
  tableHeader: {
    fontSize: "0.85rem",
    background: "#4472c4",
    paddingRight: "10px",
    fontFamily: "Roboto/Helvetica/Aria/sans-serif",
    width: "7rem",
    fontWeight: 600,
    color: "white",
  },
  tableCell: {
    fontSize: "1rem",
    whiteSpace: "normal",
    fontFamily: "Roboto/Helvetica/Aria/sans-serif",
    fontSize: "0.85rem",
    fontWeight: 400,
    width: "7rem",
  },
}));

const QuotaTabPanel = ({
  data,
  setTouched,
  tab,
  handleSubmit,
  udpatedRecords,
  columnDefs,
  domLayout,
  gridStyle = { margin: "1rem 0rem" },
}) => {
  if (data === null) {
    return <LoadingData />;
  }

  return (
    <Grid container item justifyContent="center" style={gridStyle}>
      <MaintainenenceTableDataGrid
        setTouched={setTouched}
        tab={tab}
        data={data}
        handleSubmit={handleSubmit}
        udpatedRecords={udpatedRecords}
        domLayout={domLayout}
        columnDefs={columnDefs}
      />
    </Grid>
  );
};

const CompanyQuotaPanel = ({ isDryRun }) => {
  const state = store.getState();
  const Currency_Symbol = state.app.user.Currency;
  const classes = useStyle();
  const [touched, setTouched] = useState(false);
  const dispatch = useDispatch();
  const companyQuota = useSelector(selectCompanyQuotaData);
  const udpatedRecords = useSelector(selectUpdatedRecords);

  useEffect(() => {
    return () => {
      dispatch(clearUpdateQuotaData());
    };
  }, [dispatch]);

  const handleCompanySubmit = (updatedRecords) => {
    // Transform updatedRecords into bulk upsert format
    const companyMap = {};

    updatedRecords.forEach(record => {
      const fiscalQuarter = record.FiscalQuarter;
      if (!companyMap[fiscalQuarter]) {
        companyMap[fiscalQuarter] = {
          quotaAssignments: []
        };
      }

      companyMap[fiscalQuarter].quotaAssignments.push({
        fiscalQuarter: record.FiscalQuarter,
        quota: Math.round(Number(record.Quota || record.newQuota)),
        NQP_Goal: Math.round(Number(record.NQP_GOAL || record.newNQP_GOAL)),
        canUpsert: true  // Set to true to allow upsert
      });
    });

    const bulkCompanyQuotaData = Object.values(companyMap);
    dispatch(bulkUpsertCompanyQuotaData({ bulkCompanyQuotaData }));
  };

  const editBackgroundQuota = { "edit-bg": (params) => params.data.Quota_Updated };
  const editBackgroundNQP_GOAL = { "edit-bg": (params) => params.data.NQP_GOAL_Updated };

  const companyColumnDefs = [
    { field: "FiscalQuarter", sortable: true, filter: true, flex: 1, cellStyle: { textAlign: "left" }, headerClass: classes.tableHeader },
    { field: "Quota", sortable: true, filter: true, flex: 1, type: "rightAligned", headerClass: classes.tableHeader, editable: true, valueSetter: (params) => { if (_isNaN(params.newValue) || parseInt(params.newValue) < 0 || params.newValue === "") { window.alert("Quota must be a valid positive number"); return false; } params.data.Quota = params.newValue; return true; }, cellClassRules: editBackgroundQuota, valueFormatter: (x) => `${Currency_Symbol} ${d3.format(",")(Math.round(x.value))}` },
    { field: "NQP_GOAL", headerName: "NQP Goal", sortable: true, filter: true, tooltipField: "NQP_GOAL", flex: 1, type: "rightAligned", headerClass: classes.tableHeader, editable: true, valueSetter: (params) => { if (_isNaN(params.newValue) || parseInt(params.newValue) < 0) { window.alert("NQP goal must be a positive number"); return false; } params.data.NQP_GOAL = params.newValue; return true; }, cellClassRules: editBackgroundNQP_GOAL, resizable: false, valueFormatter: (x) => `${Currency_Symbol} ${d3.format(",")(Math.round(x.value))}` },
  ];

  return (
    <QuotaTabPanel
      data={companyQuota}
      setTouched={setTouched}
      tab="company"
      handleSubmit={handleCompanySubmit}
      udpatedRecords={udpatedRecords}
      columnDefs={companyColumnDefs}
      domLayout="autoHeight"
    />
  );
};

export default CompanyQuotaPanel;
